import { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import AddMediaDialog from "aldoo-ra/AddMediaDialog"
import { UploadCMSFile } from "aldoo-ra/CMS/api"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import ColorPickerEditor from "aldoo-ra/CMS/field-editors/color-picker"
import { PhotoIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { openGoPremium } from "../GoPremiumDialog"
import { SaveTheme } from "../FileShareFeature/api"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"

// Sensible starting palette (matches the default dark share page look) so a
// brand-new theme renders something coherent before the user picks colors.
const DEFAULTS = {
  color_background: "#181818",
  color_text: "#FFFFFF",
  color_primary: "#FF6B00",
  color_button_text: "#FFFFFF",
}

// Create/edit dialog for a UserShareTheme. Mirrors the CMS content editor —
// reuses the same Image + ColorPicker field editors — but scoped to the four
// fields a share theme exposes. On save it returns the persisted theme via
// onSaved so callers (gallery / file-options dropdown) can select it.
export default function ThemeEditorDialog({ theme, onClose, onSaved }) {
  const editing = !!theme?._id
  const c = theme?.content || theme || {}

  const [name, setName] = useState(c.name || "")
  const [bg, setBg] = useState(c.background_image || null)
  const [colors, setColors] = useState({
    color_background: c.color_background || DEFAULTS.color_background,
    color_text: c.color_text || DEFAULTS.color_text,
    color_primary: c.color_primary || DEFAULTS.color_primary,
    color_button_text: c.color_button_text || DEFAULTS.color_button_text,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  // Dedicated upload/crop dialog instead of the CMS media library — the
  // library jumps the user out of the theme editor flow and surfaces every
  // image they've ever uploaded. AddMediaDialog focuses on drop / pick /
  // crop and returns a single key via its uploadFn.
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false)

  const texts = TextBundle({ id: "transfer_theme_editor" })
  const { translateWithLanguage, getLanguage } = Localization()
  const t = (key, data) => translateWithLanguage(key, getLanguage(), data)

  const COLOR_ROWS = [
    { key: "color_background", label: texts?.color_background },
    { key: "color_text", label: texts?.color_text },
    { key: "color_primary", label: texts?.color_primary },
    { key: "color_button_text", label: texts?.color_button_text },
  ]

  const setColor = (key, value) => setColors((p) => ({ ...p, [key]: value }))

  // AddMediaDialog hands us the cropped blob; push it through the standard
  // CMS file-upload endpoint and surface the resulting path as the theme's
  // background_image. Stored under the dedicated `user-share-backgrounds/`
  // category so user-uploaded theme assets don't mix with editorial CMS
  // images. File-size / format limits are declared on the CMS type field
  // (`maxFileSize`, `formats`) and enforced by the standard CMS pipeline —
  // no custom client-side validator here.
  const uploadFn = async (blob, mimeType) => {
    const ext = (mimeType || '').split('/')[1] || 'png'
    const file = new File([blob], `theme-bg.${ext}`, { type: mimeType || 'image/png' })
    const fd = new FormData()
    fd.append('file', file)
    fd.append('category', 'user-share-backgrounds')
    const res = await UploadCMSFile(fd, () => {})
    return { path: res?.path }
  }

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError(texts?.error_name_required)
      return
    }
    setSaving(true)
    setError("")
    const res = await SaveTheme({
      ...(editing ? { _id: theme._id } : {}),
      name: trimmed,
      background_image: bg || null,
      ...colors,
    }).catch(() => ({ error: "network" }))
    setSaving(false)

    if (res?.error === "feature_locked") {
      onClose?.()
      openGoPremium()
      return
    }
    if (res?.error === "theme_limit_reached") {
      setError(t(texts?.error_theme_limit_fmt, { limit: res.limit || 50 }))
      return
    }
    if (res?.error || !res?.theme) {
      setError(texts?.error_save_failed)
      return
    }
    onSaved?.(res.theme)
  }

  return (
    <Modal
      variant="transfer_medium"
      isOpen={true}
      onClose={onClose}
      allowCloseBtn={true}
      title={editing ? texts?.edit_title : texts?.new_title}
      // Save / Cancel live in footerContent so the modal pins them to the
      // bottom (sticky) — important on mobile where the form's scrollable
      // area would otherwise push the actions off-screen.
      footerContent={
        <div className="flex justify-end gap-2 w-full">
          <Button
            variant="secondary"
            onClick={onClose} disabled={saving}
          >
            {texts?.cancel_btn}
          </Button>

          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? texts?.saving_btn : editing ? texts?.save_btn : texts?.create_btn}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-1">
        <div>
          <Typography variant="label_dim">
            {texts?.theme_name_label}
          </Typography>

          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={texts?.theme_name_ph}
            maxLength={80}
          />
        </div>

        {/* Colors before the background image — the picker's popover opens
            downward, so giving it space below avoids being clipped by the
            modal's edge. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {COLOR_ROWS.map(({ key, label }) => (
            <div key={key}>
              <Typography variant="label_dim">
                {label}
              </Typography>

              <ColorPickerEditor
                field={{ id: key, label }}
                value={colors[key]}
                onChange={(v) => setColor(key, v)}
              />
            </div>
          ))}
        </div>

        <div>
          <Typography variant="label_dim">
            {texts?.bg_image_label}
          </Typography>

          {bg ? (
            <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden border border-bordered">
              <img src={cmsUrl(bg)} alt="" className="absolute inset-0 w-full h-full object-cover" />

              {/* Clear button — floats top-right above the image so it's always
                  reachable without competing for the Replace button's space. */}
              <button
                type="button"
                onClick={() => setBg(null)}
                title={texts?.clear_image_title}
                className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>

              <div className="absolute inset-x-0 bottom-0 flex justify-end p-2 bg-gradient-to-t from-black/40 to-transparent">
                <Button
                  variant="primary_s"
                  onClick={() => setMediaDialogOpen(true)}
                >
                  {texts?.replace_btn}
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setMediaDialogOpen(true)}
              className="w-full aspect-[16/9] rounded-xl border-2 border-dashed border-bordered hover:border-primary text-text_secondary hover:text-text flex flex-col items-center justify-center gap-2 transition-colors"
            >
              <PhotoIcon className="h-8 w-8" />
              <Typography variant="txt1_dim">
                {texts?.add_bg_image}
              </Typography>
            </button>
          )}
        </div>

        {/* Live preview of the chosen palette */}
        <div
          className="rounded-xl border border-bordered p-4 flex items-center gap-3"
          style={{ background: colors.color_background, color: colors.color_text }}
        >
          <span className="text-sm font-medium">{texts?.preview_label}</span>
          <span
            className="px-3 py-1 rounded-sm text-sm font-semibold"
            style={{ background: colors.color_primary, color: colors.color_button_text }}
          >
            {texts?.preview_download}
          </span>
        </div>

        {error ? (
          <Typography variant="txt1" className="text-error">
            {error}
          </Typography>
        ) : null}
      </div>

      {mediaDialogOpen && (
        <AddMediaDialog
          imagesOnly
          currentImageUrl={bg ? cmsUrl(bg) : null}
          uploadFn={uploadFn}
          onUse={(result) => {
            setMediaDialogOpen(false)
            if (result?.path) setBg(result.path)
          }}
          onCancel={() => setMediaDialogOpen(false)}
        />
      )}
    </Modal>
  )
}
